import Alpine from "alpinejs";
import { registerConsentStore } from "./ConsentStore.js";
window.Alpine = Alpine;

// Global cookie consent store, backed by CookieFirst - defined immediately so it's
// available when Alpine starts. See ConsentStore.js for the CookieFirst contract.
registerConsentStore(Alpine);

document.addEventListener('alpine:init', () => {
    // Lazy video loading - loads video when it enters viewport
    Alpine.data('lazyVideo', () => ({
        _observer: null,
        observe() {
            if (!('IntersectionObserver' in window)) {
                // Fallback: load immediately if IntersectionObserver not supported
                this.load();
                return;
            }
            this._observer = new IntersectionObserver((entries, obs) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.load();
                        obs.unobserve(entry.target);
                    }
                });
            }, {
                rootMargin: '200px 0px' // Start loading 200px before entering viewport
            });
            this._observer.observe(this.$el);
        },
        load() {
            const video = this.$el;
            video.preload = 'metadata';
            video.load();
        },
        destroy() {
            if (this._observer) {
                this._observer.disconnect();
            }
        }
    }));

    // Play on hover (desktop) / play in viewport (mobile)
    Alpine.data('hoverVideo', () => ({
        _observer: null,
        _isMobile: false,
        _isLoaded: false,
        init() {
            // Detect mobile/touch device
            this._isMobile = window.matchMedia('(max-width: 1024px)').matches ||
                             'ontouchstart' in window ||
                             navigator.maxTouchPoints > 0;

            const video = this.$el;
            video.preload = 'metadata';

            if (this._isMobile) {
                this._setupMobileObserver();
            }
        },
        _setupMobileObserver() {
            if (!('IntersectionObserver' in window)) {
                return;
            }
            this._observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this._loadAndPlay();
                    } else {
                        this.pause();
                    }
                });
            }, {
                threshold: 0.5 // Play when 50% visible
            });
            this._observer.observe(this.$el);
        },
        _loadAndPlay() {
            const video = this.$el;
            if (!this._isLoaded) {
                video.load();
                this._isLoaded = true;
            }
            video.play().catch(() => {
                // Autoplay blocked - silently fail
            });
        },
        play() {
            if (this._isMobile) return; // Mobile uses observer
            this._loadAndPlay();
        },
        pause() {
            const video = this.$el;
            if (!video.paused) {
                video.pause();
            }
        },
        destroy() {
            if (this._observer) {
                this._observer.disconnect();
            }
        }
    }));

    Alpine.data('page', () => ({
        // state
        isOpenMobileMenu: false,
        overflowHidden: false,
        openMenuPages: [],
        consents: [],

        // ==== INIT: lazy background loader ====
        init() {
            const root = this.$root || document;
            const lazyBackgroundImages = Array.from(root.querySelectorAll('.lazyloading-bg'));

            if ('IntersectionObserver' in window) {
                const observer = new IntersectionObserver((entries, obs) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            // once visible, drop the blocking class so the bg-image can apply
                            entry.target.classList.remove('lazyloading-bg');
                            obs.unobserve(entry.target);
                        }
                    });
                }, {
                    // start a bit early so images are ready when you scroll to them
                    rootMargin: '200px 0px'
                });

                lazyBackgroundImages.forEach((el) => observer.observe(el));
                // keep a ref if you ever want to disconnect manually
                this._lazyBgObserver = observer;
            } else {
                // Fallback: no IO support → just load immediately
                lazyBackgroundImages.forEach((el) => el.classList.remove('lazyloading-bg'));
            }

            this.initRteFoldouts();
            this.scrollToInitialAnchor();
        },
        scrollToInitialAnchor() {
            const hash = window.location.hash;
            if (!hash || hash.length < 2) {
                return;
            }

            const raw = hash.slice(1);
            const decoded = decodeURIComponent(raw);
            const cssEscape = (value) => {
                if (window.CSS && typeof window.CSS.escape === 'function') {
                    return window.CSS.escape(value);
                }
                return value.replace(/["\\]/g, '\\$&');
            };

            const target =
                document.getElementById(decoded) ||
                document.querySelector(`[name="${cssEscape(decoded)}"]`);

            if (!target) {
                return;
            }

            const prefersReduced = window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            const performScroll = () => {
                try {
                    target.scrollIntoView({
                        behavior: prefersReduced ? 'auto' : 'smooth',
                        block: 'start'
                    });
                } catch (err) {
                    target.scrollIntoView();
                }
            };

            requestAnimationFrame(() => {
                setTimeout(performScroll, 0);
            });
        },
        initRteFoldouts() {
            const root = this.$root || document.body;
            if (!root) {
                return;
            }

            const openLabel = root.dataset.rteFoldoutOpen || 'Show more';
            const closeLabel = root.dataset.rteFoldoutClose || 'Show less';

            const markers = Array.from(root.querySelectorAll('.fold-break, .page-break'));
            markers.forEach((marker) => {
                const parent = marker.parentNode;
                if (!parent || parent.nodeType !== 1) {
                    return;
                }

                const foldout = document.createElement('div');
                foldout.className = 'rte-foldout';
                foldout.dataset.foldOpenLabel = marker.dataset.foldOpen || '';
                foldout.dataset.foldCloseLabel = marker.dataset.foldClose || '';

                let sibling = marker.nextSibling;
                while (sibling) {
                    const nextSibling = sibling.nextSibling;
                    foldout.appendChild(sibling);
                    sibling = nextSibling;
                }

                const foldoutContainer = document.createElement('div');
                foldoutContainer.className = 'rte-foldout-container';
                const foldoutInner = document.createElement('div');
                foldoutInner.className = 'rte-foldout-inner';
                while (foldout.firstChild) {
                    foldoutInner.appendChild(foldout.firstChild);
                }
                foldoutContainer.appendChild(foldoutInner);
                foldout.appendChild(foldoutContainer);

                parent.insertBefore(foldout, marker.nextSibling);
                marker.remove();
            });

            root.querySelectorAll('.rte-foldout').forEach((foldout) => {
                if (foldout.dataset.rteFoldoutInitialized) {
                    return;
                }

                const buttonWrap = document.createElement('div');
                buttonWrap.className = 'rte-foldout-toggle-wrap';
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'button-ghost-tiny color-1';
                buttonWrap.appendChild(button);

                const bottomButtonWrap = document.createElement('div');
                bottomButtonWrap.className = 'rte-foldout-toggle-wrap rte-foldout-toggle-wrap--bottom';
                const bottomButton = document.createElement('button');
                bottomButton.type = 'button';
                bottomButton.className = 'button-ghost-tiny color-1';
                bottomButtonWrap.appendChild(bottomButton);

                const foldoutOpenLabel = foldout.dataset.foldOpenLabel || openLabel;
                const foldoutCloseLabel = foldout.dataset.foldCloseLabel || closeLabel;

                const setState = (isOpen) => {
                    foldout.classList.toggle('is-open', isOpen);
                    button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
                    bottomButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
                    button.textContent = isOpen ? foldoutCloseLabel : foldoutOpenLabel;
                    bottomButton.textContent = foldoutCloseLabel;
                    bottomButtonWrap.hidden = !isOpen;
                    buttonWrap.hidden = isOpen;
                };

                setState(false);
                const toggle = () => setState(!foldout.classList.contains('is-open'));
                button.addEventListener('click', toggle);
                bottomButton.addEventListener('click', toggle);

                foldout.dataset.rteFoldoutInitialized = '1';
                foldout.parentNode.insertBefore(buttonWrap, foldout);
                foldout.parentNode.insertBefore(bottomButtonWrap, foldout.nextSibling);
            });
        },
        hasConsent(requirements = []) {
            return Alpine.store('consent').has(requirements);
        },
        allowConsents(consents) {
            Alpine.store('consent').allow(consents);
        },
        // ==== mobile menu ====
        toggleMobileMenu() {
            if (this.isOpenMobileMenu) {
                this.closeMobileMenu();
            } else {
                this.openMobileMenu();
            }
        },
        closeMobileMenu() {
            this.isOpenMobileMenu = false;
            this.overflowHidden = false;
            this.closeAllMenues();
        },
        openMobileMenu() {
            this.isOpenMobileMenu = true;
            this.overflowHidden = true;
        },

        // ==== subpages ====
        toggleMenuSubpage(id) {
            if (this.isMenuOpen(id)) {
                this.closeMenuSubpage(id);
            } else {
                this.openMenuSubpage(id);
            }
        },
        openMenuSubpage(id) {
            this.openMenuPages.push(id);
        },
        closeMenuSubpage(id) {
            this.openMenuPages = this.openMenuPages.filter((item) => item !== id);
        },
        closeAllMenues() {
            this.openMenuPages = [];
        },
        isMenuOpen(id) {
            return this.openMenuPages.includes(id);
        },
    }));

    Alpine.data('desktopMenuItem', () => ({
        open: false,
        toggleMenu() { this.open = !this.open },
        closeMenu()  { this.open = false },
        openMenu()   { this.open = true }
    }));

    // Header2 component - two-tier header with scroll effects
    Alpine.data('header2', () => ({
        scrolled: false,
        topBarVisible: true,
        mobileMenuOpen: false,
        openMenuPages: [],

        init() {
            this.handleScroll();
            window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
        },

        handleScroll() {
            const scrollY = window.scrollY;
            this.scrolled = scrollY > 50;
            this.topBarVisible = scrollY < 100;
        },

        toggleMobileMenu() {
            this.mobileMenuOpen = !this.mobileMenuOpen;
            document.body.style.overflow = this.mobileMenuOpen ? 'hidden' : '';
        },

        closeMobileMenu() {
            this.mobileMenuOpen = false;
            document.body.style.overflow = '';
            this.closeAllMenues();
        },

        toggleMenuSubpage(id) {
            if (this.isMenuOpen(id)) {
                this.closeMenuSubpage(id);
            } else {
                this.openMenuSubpage(id);
            }
        },

        openMenuSubpage(id) {
            this.openMenuPages.push(id);
        },

        closeMenuSubpage(id) {
            this.openMenuPages = this.openMenuPages.filter((item) => item !== id);
        },

        closeAllMenues() {
            this.openMenuPages = [];
        },

        isMenuOpen(id) {
            return this.openMenuPages.includes(id);
        }
    }));

    // Newsletter subscription component
    Alpine.data('newsletter', () => ({
        email: '',
        status: 'idle', // idle, loading, success, error
        errorMessage: '',

        async subscribe() {
            if (!this.email || this.status === 'loading') return;

            this.status = 'loading';
            this.errorMessage = '';

            try {
                const form = this.$el.querySelector('form');
                const action = form.getAttribute(':action')?.replace(/'/g, '') || form.action;

                if (!action) {
                    // If no action defined, just show success for demo
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    this.status = 'success';
                    return;
                }

                const response = await fetch(action, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams({ email: this.email })
                });

                if (response.ok) {
                    this.status = 'success';
                    this.email = '';
                } else {
                    throw new Error('Subscription failed');
                }
            } catch (error) {
                this.status = 'error';
                this.errorMessage = error.message || 'An error occurred. Please try again.';
                setTimeout(() => {
                    this.status = 'idle';
                }, 3000);
            }
        }
    }));
})

if (document.readyState === 'complete') {
    Alpine.start();
} else {
    window.addEventListener('load', () => Alpine.start(), { once: true });
}
